import { ApplicationRequirementsStore } from 'ng2-app/modules/borrower/models/application-requirements/application-requirements.store';
import { AttributeViewState } from 'ng2-app/modules/borrower/components/borrower-details/app-requirements/app-requirements.component';
import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { RequirementField, RequirementIssue } from 'ng2-app/modules/borrower/models/application-requirements/application-requirements.interface';
import { BankStatementRequirement, ValueRequirement, DisplayableInfo } from 'ng2-app/modules/borrower/models/application-requirements/application-requirements.interface';
import { trigger, style, animate, transition } from '@angular/animations';
import { first, finalize, map, take } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { BorrowerState } from 'app/store/borrower/borrower/borrower.state';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { Borrower } from 'app/models/dto/borrower.model';

export interface IgnoreEvent {
    requirementId: number;
    ignored: boolean;
}

@Component({
    selector: 'app-requirement-collapse',
    templateUrl: 'app-requirement-collapse.component.html',
    styleUrls: ['app-requirement-collapse.component.scss'],
    animations: [
        trigger('expandInOut', [
            transition('* => void', [
                style({ height: '*', overflow: 'hidden' }),
                animate(300, style({ height: 0 }))
            ]),
            transition('void => *', [
                style({ height: '0', overflow: 'hidden' }),
                animate(300, style({ height: '*' }))
            ])
        ])
    ]
})

export class AppRequirementCollapseComponent implements OnInit, OnDestroy {

    @Select(BorrowerState.borrower) borrower$: Observable<Borrower>;
    @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

    @Input() appRequirement: ValueRequirement;
    @Input() bankRequirements: BankStatementRequirement[];
    @Input() bankRequirementStatus: string;
    @Input() borrowerId: number;
    @Input() viewState: { [key: string]: AttributeViewState };

    @Output() met = new EventEmitter<void>();
    @Output() ignored = new EventEmitter<IgnoreEvent>();

    canEdit$: Observable<boolean>;
    togglingIgnored: any;
    displayableInfo: DisplayableInfo[] | undefined;
    fields: RequirementField[];

    constructor(
        private appRequirementsStore: ApplicationRequirementsStore
    ) { }

    ngOnInit() {
        if (this.appRequirement && this.appRequirement.fields) {
            this.fields = this.appRequirement.fields.filter((field) => {
                if (field.alias) {
                    return !(field.alias.includes('phone') || field.alias.includes('Phone'));
                }
                return true;
            });
        }

        // Initialize view state if not already set
        if (!this.viewState.hasOwnProperty(this.attributeKey)) {
            this.viewState[this.attributeKey] = { expanded: false };
        }

        this.togglingIgnored = {};

        // see if the currentUser is assigned to the borrower
        const isRepBorrowerOwner$ = combineLatest([
            this.lenderUser$.pipe(map((currentUser) => currentUser.id)),
            this.borrower$.pipe(map((borrowerOwner) => borrowerOwner?.userId))
        ]).pipe(
            map(([currentUserId, borrowerOwnerId]) => {
                return borrowerOwnerId ? borrowerOwnerId === currentUserId :false;
            })
        );

        // see if the currentUser has permissions to override
        const canOverrideAppReqs$ = this.lenderUser$.pipe(
            map((currentUser) => currentUser.permissions.includes('overrideAppReqsForSend'))
        );

        // combine our checks to determine canEdit bool
        this.canEdit$ = combineLatest([
            isRepBorrowerOwner$,
            canOverrideAppReqs$
        ]).pipe(
            map(([canOverride, isOwner]) => canOverride || isOwner),
            take(1)
        );

        // TODO: Can this be undefined on init?
        this.displayableInfo = this.appRequirement ? this.appRequirement.displayableInfo : undefined;
    }

    ngOnDestroy() {
        this.expanded = false;
    }

    get expanded() {
        if (!this.viewState || (!this.appRequirement && !this.bankRequirements)) {
            return false;
        }
        return this.viewState[this.attributeKey].expanded;
    }
    set expanded(expanded: boolean) {
        this.viewState[this.attributeKey].expanded = expanded;
    }

    get attributeKey() {
        return (this.appRequirement && this.appRequirement.key) || 'bankStatements';
    }

    toggleExpanded() {
        this.expanded = !this.expanded;
    }

    toggleIgnored(issue: RequirementIssue) {
        this.togglingIgnored[issue.requirementId] = true;
        issue.ignored = !issue.ignored;
        this.ignored.next({
            requirementId: issue.requirementId,
            ignored: issue.ignored
        });
        this.appRequirementsStore.toggleIssueIgnored(
            this.borrowerId,
            issue.requirementId,
            issue.ignored
        ).pipe(
            finalize(() => this.togglingIgnored[issue.requirementId] = false),
            first()
        )
            .subscribe();
    }
}
